import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { jobsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ScoringSettings } from '../components/job/ScoringSettings';
import { JDQualityCard } from '../components/job/JDQualityCard';
import { formatSalary, CURRENCIES, SALARY_PERIODS, usesLakhInput, toAbsolute, toInputUnit, inputHint } from '../lib/salary';
import { RichTextEditor } from '../components/RichTextEditor';
import { toPlainText } from '../lib/html';
import { toast } from 'sonner';

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', required_skills: '', nice_to_have_skills: '',
    experience_years: 0, location: '', job_type: 'full-time',
    salary_min: '', salary_max: '', salary_currency: 'USD', salary_period: 'year',
    benchmark_screening: 60, benchmark_assessment: 70, benchmark_interview: 75, benchmark_bgv: 75,
    bias_blind_scoring: false, high_accuracy_scoring: false, score_weights: null
  });
  const [creating, setCreating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedFile, setParsedFile] = useState(null);
  const [step, setStep] = useState('upload');
  const [extractingSkills, setExtractingSkills] = useState(false);
  const [filters, setFilters] = useState({ minSalary: '', hasSalary: false });
  const extractTimerRef = useRef(null);
  const navigate = useNavigate();

  const loadJobs = useCallback(async () => {
    try {
      const params = {};
      if (filters.hasSalary) params.has_salary = true;
      if (filters.minSalary) params.min_salary = parseInt(filters.minSalary) || undefined;
      const res = await jobsAPI.list(params);
      setJobs(res.data);
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const resetDialog = () => {
    setForm({ title: '', description: '', required_skills: '', nice_to_have_skills: '', experience_years: 0, location: '', job_type: 'full-time', salary_min: '', salary_max: '', salary_currency: 'USD', salary_period: 'year', benchmark_screening: 60, benchmark_assessment: 70, benchmark_interview: 75, benchmark_bgv: 75, bias_blind_scoring: false, high_accuracy_scoring: false, score_weights: null });
    setParsedFile(null);
    setStep('upload');
    setParsing(false);
    setExtractingSkills(false);
    if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
  };

  // Auto-extract skills when description changes (debounced)
  const handleDescriptionChange = (text) => {
    setForm(prev => ({ ...prev, description: text }));
    if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
    const plain = toPlainText(text);
    if (plain.trim().length > 50) {
      extractTimerRef.current = setTimeout(async () => {
        setExtractingSkills(true);
        try {
          const res = await jobsAPI.extractSkills(plain);
          const required = (res.data.required_skills || []).join(', ');
          const nice = (res.data.nice_to_have_skills || []).join(', ');
          setForm(prev => ({
            ...prev,
            required_skills: prev.required_skills || required,
            nice_to_have_skills: prev.nice_to_have_skills || nice,
          }));
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Skill extraction failed:', err);
        } finally { setExtractingSkills(false); }
      }, 1500);
    }
  };

  // When the currency changes, convert the entered range so its real value is preserved
  // (INR is entered in Lakhs, other currencies in absolute units).
  const handleSalaryCurrencyChange = (newCur) => {
    setForm(prev => {
      const absMin = toAbsolute(prev.salary_min, prev.salary_currency);
      const absMax = toAbsolute(prev.salary_max, prev.salary_currency);
      return {
        ...prev, salary_currency: newCur,
        salary_min: absMin != null ? toInputUnit(absMin, newCur) : '',
        salary_max: absMax != null ? toInputUnit(absMax, newCur) : '',
      };
    });
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setParsing(true);
    setParsedFile(file.name);

    try {
      const res = await jobsAPI.parseJD(file);
      const data = res.data;

      setForm({
        title: data.title || '',
        description: data.description || data.raw_text || '',
        required_skills: (data.required_skills || []).join(', '),
        nice_to_have_skills: (data.nice_to_have_skills || []).join(', '),
        experience_years: data.experience_years || 0,
        location: data.location || '',
        job_type: data.job_type || 'full-time',
        salary_min: '', salary_max: '', salary_currency: 'USD', salary_period: 'year',
      });

      if (data.parse_error) {
        toast.info('AI parsing had an issue. The raw text has been loaded — please fill in the details manually.');
      } else {
        toast.success('JD parsed successfully! Review and edit the details below.');
      }
      setStep('form');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse JD file');
    } finally {
      setParsing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: false,
    disabled: parsing,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !toPlainText(form.description).trim()) {
      toast.error('Title and description are required');
      return;
    }
    const absMin = toAbsolute(form.salary_min, form.salary_currency);
    const absMax = toAbsolute(form.salary_max, form.salary_currency);
    if (absMin != null && absMax != null && absMin > absMax) {
      toast.error('Minimum salary cannot exceed the maximum');
      return;
    }
    setCreating(true);
    try {
      const jobData = {
        ...form,
        required_skills: form.required_skills ? form.required_skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        nice_to_have_skills: form.nice_to_have_skills ? form.nice_to_have_skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        experience_years: parseInt(form.experience_years) || 0,
        salary_min: absMin,
        salary_max: absMax,
        benchmark_screening: parseInt(form.benchmark_screening) || 0,
        benchmark_assessment: parseInt(form.benchmark_assessment) || 0,
        benchmark_interview: parseInt(form.benchmark_interview) || 0,
        benchmark_bgv: parseInt(form.benchmark_bgv) || 0
      };
      await jobsAPI.create(jobData);
      toast.success('Job created successfully');
      setShowCreate(false);
      resetDialog();
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreate = () => {
    resetDialog();
    setShowCreate(true);
  };

  const inputStyle = {
    width: '100%', padding: '0.625rem 0.875rem', borderRadius: 10,
    border: '1px solid #CBD5E1', background: '#FAF7F2',
    color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box'
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'hsl(var(--muted-foreground))' }}>Loading jobs...</div>;
  }

  return (
    <div data-testid="jobs-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Jobs</h1>
          <p className="page-subtitle">{jobs.length} job{jobs.length !== 1 ? 's' : ''} posted</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={() => navigate('/jd-builder')} variant="outline" style={{ borderRadius: 10, padding: '0.625rem 1.25rem', fontWeight: 600, borderColor: '#0076A3', color: '#0076A3' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            Build JD with AI
          </Button>
          <Button data-testid="create-job-button" onClick={handleOpenCreate} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, padding: '0.625rem 1.25rem', fontWeight: 600 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Create Job
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div data-testid="jobs-filter-bar" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', margin: '0 0 1.25rem', padding: '0.75rem 1rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 12 }}>
        <span style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B' }}>Filter</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: '#334155', cursor: 'pointer' }}>
          <input data-testid="filter-has-salary" type="checkbox" checked={filters.hasSalary} onChange={(e) => setFilters({ ...filters, hasSalary: e.target.checked })} style={{ accentColor: '#0076A3', width: 15, height: 15 }} />
          Salary posted
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.8125rem', color: '#64748B' }}>Min salary</span>
          <input data-testid="filter-min-salary" type="number" min={0} value={filters.minSalary} onChange={(e) => setFilters({ ...filters, minSalary: e.target.value })} placeholder="e.g. 100000" style={{ width: 130, padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', fontSize: '0.8125rem', outline: 'none' }} />
        </div>
        {(filters.hasSalary || filters.minSalary) && (
          <button data-testid="filter-clear" onClick={() => setFilters({ minSalary: '', hasSalary: false })} style={{ marginLeft: 'auto', padding: '0.35rem 0.85rem', borderRadius: 999, border: '1px solid #ECE7DE', background: '#fff', color: '#64748B', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Clear</button>
        )}
      </div>

      {jobs.length === 0 ? (
        (filters.hasSalary || filters.minSalary) ? (
          <div className="empty-state" style={{ paddingTop: '4rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: '#002A38' }}>No jobs match this filter</h3>
            <p style={{ marginBottom: '1.5rem' }}>Try lowering the minimum salary or clearing the filter.</p>
            <Button data-testid="filter-clear-empty" onClick={() => setFilters({ minSalary: '', hasSalary: false })} variant="outline" style={{ borderRadius: 999, padding: '0.625rem 1.25rem', fontWeight: 600 }}>Clear filter</Button>
          </div>
        ) : (
        <div className="empty-state" style={{ paddingTop: '6rem' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 64, height: 64, marginBottom: '1.5rem' }}>
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: '#002A38' }}>No jobs yet</h3>
          <p style={{ marginBottom: '1.5rem' }}>Create your first job posting to start screening candidates.</p>
          <Button onClick={handleOpenCreate} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, padding: '0.625rem 1.25rem', fontWeight: 600 }}>Create First Job</Button>
        </div>
        )
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1rem' }}>
          {jobs.map((job) => (
            <Card
              key={job._id}
              data-testid="job-card"
              onClick={() => navigate(`/jobs/${job._id}`)}
              style={{
                background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14,
                padding: '1.25rem', cursor: 'pointer', transition: 'border-color 0.2s'
              }}
              className="candidate-row"
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.3 }}>{job.title}</h3>
                <Badge variant={job.status === 'active' ? 'default' : 'secondary'}
                  style={{ background: job.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: job.status === 'active' ? '#059669' : '#DC2626', border: `1px solid ${job.status === 'active' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`, borderRadius: 999, fontSize: '0.6875rem', textTransform: 'capitalize' }}>
                  {job.status}
                </Badge>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {job.description}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '1rem' }}>
                {(job.required_skills || []).slice(0, 5).map((skill) => (
                  <span key={skill} style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(240, 80, 0, 0.1)', border: '1px solid rgba(240, 80, 0, 0.2)', fontSize: '0.6875rem', color: '#0076A3' }}>{skill}</span>
                ))}
                {(job.required_skills || []).length > 5 && (
                  <span style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'hsl(var(--muted))', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>+{job.required_skills.length - 5}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                <span>{job.candidate_count || 0} candidates</span>
                {formatSalary(job) ? (
                  <span data-testid="job-card-salary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.125rem 0.5rem', borderRadius: 999, background: '#CCFBF1', color: '#0F766E', fontWeight: 700, fontSize: '0.6875rem' }}>{formatSalary(job)}</span>
                ) : (
                  <span>{job.location || 'Remote'} &middot; {job.job_type}</span>
                )}
              </div>
              {/* Job Actions */}
              <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.75rem', borderTop: '1px solid #F0EBE3', paddingTop: '0.75rem' }}>
                <button onClick={(e) => {
                  e.stopPropagation();
                  const newPriority = job.priority === 'high' ? 'normal' : 'high';
                  jobsAPI.update(job._id, { priority: newPriority }).then(() => { toast.success(newPriority === 'high' ? 'Marked as priority' : 'Priority removed'); loadJobs(); });
                }} style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: `1px solid ${job.priority === 'high' ? '#FDE68A' : '#ECE7DE'}`, background: job.priority === 'high' ? '#FFFBEB' : '#fff', cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: job.priority === 'high' ? '#D97706' : '#94A3B8' }}>
                  {job.priority === 'high' ? '\u2605 Priority' : '\u2606 Priority'}
                </button>
                <button onClick={(e) => {
                  e.stopPropagation();
                  jobsAPI.update(job._id, { status: 'archived' }).then(() => { toast.success('Job archived'); loadJobs(); });
                }} style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid #ECE7DE', background: '#fff', cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: '#64748B' }}>
                  Archive
                </button>
                <button onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete "${job.title}"? This cannot be undone.`)) {
                    jobsAPI.delete(job._id).then(() => { toast.success('Job deleted'); loadJobs(); });
                  }
                }} style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid #FECACA', background: '#FEF2F2', cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: '#DC2626' }}>
                  Delete
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Job Dialog — Two Steps */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetDialog(); }}>
        <DialogContent style={{ background: '#fff', border: '1px solid #CBD5E1', borderRadius: 14, maxWidth: step === 'upload' ? 520 : 620, maxHeight: '90vh', overflowY: 'auto', transition: 'max-width 0.2s' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem' }}>
              {step === 'upload' ? 'Create New Job' : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Create New Job
                  {parsedFile && (
                    <span style={{ fontSize: '0.6875rem', fontWeight: 400, color: '#0076A3', padding: '0.125rem 0.5rem', borderRadius: 999, background: 'rgba(240, 80, 0, 0.1)', border: '1px solid rgba(240, 80, 0, 0.2)' }}>
                      Auto-filled from {parsedFile}
                    </span>
                  )}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {step === 'upload' ? (
            /* Step 1: Upload JD or skip */
            <div>
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                Upload a Job Description file and AI will automatically extract the title, skills, requirements, and other details.
              </p>

              <div {...getRootProps()} className={`upload-dropzone ${isDragActive ? 'active' : ''}`} data-testid="jd-upload-dropzone" style={{ marginBottom: '1.25rem' }}>
                <input {...getInputProps()} data-testid="jd-upload-input" />
                {parsing ? (
                  <>
                    <span className="status-dot status-processing" style={{ display: 'block', margin: '0 auto 0.75rem', width: 12, height: 12 }} />
                    <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.25rem' }}>Parsing with AI...</p>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Extracting title, skills, requirements</p>
                  </>
                ) : (
                  <>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 0.75rem' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/>
                    </svg>
                    <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#002A38', marginBottom: '0.25rem' }}>
                      {isDragActive ? 'Drop JD file here...' : 'Upload Job Description'}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      PDF, DOCX, or TXT &middot; AI will auto-fill all fields
                    </p>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
                <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>OR</span>
                <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
              </div>

              <Button
                data-testid="manual-entry-button"
                variant="outline"
                onClick={() => setStep('form')}
                style={{ width: '100%', borderRadius: 10, padding: '0.75rem' }}
              >
                Enter details manually
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate('/jd-builder')}
                style={{ width: '100%', borderRadius: 10, padding: '0.75rem', marginTop: '0.5rem', borderColor: '#0076A3', color: '#0076A3' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                Build JD with AI
              </Button>
            </div>
          ) : (
            /* Step 2: Review/Edit form */
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {parsedFile && (
                <div style={{ padding: '0.625rem 0.875rem', borderRadius: 10, background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)', fontSize: '0.75rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Fields auto-filled from <strong>{parsedFile}</strong>. Review and edit as needed.
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Job Title *</label>
                <input data-testid="job-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Senior Full-Stack Developer" required style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Job Description *</label>
                <RichTextEditor testId="job-description-input" value={form.description} onChange={handleDescriptionChange} placeholder="Describe the role, responsibilities, and requirements…" />
                {extractingSkills && <div style={{ fontSize: '0.6875rem', color: '#0076A3', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}><span className="status-dot status-processing" style={{ width: 6, height: 6 }} /> Extracting skills from description...</div>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Required Skills (comma-separated)</label>
                  <input data-testid="job-skills-input" value={form.required_skills} onChange={(e) => setForm({ ...form, required_skills: e.target.value })} placeholder="Python, React, AWS" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Nice-to-have Skills</label>
                  <input value={form.nice_to_have_skills} onChange={(e) => setForm({ ...form, nice_to_have_skills: e.target.value })} placeholder="GraphQL, Docker" style={inputStyle} />
                </div>
              </div>
              <div style={{ padding: '0.875rem 1rem', borderRadius: 12, border: '1px solid hsl(var(--border))', background: '#FAF7F2' }}>
                <JDQualityCard job={form} compact />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Experience (years)</label>
                  <input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} min="0" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Location</label>
                  <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Remote" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Job Type</label>
                  <select value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} style={inputStyle}>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' }}>Salary Range (optional){usesLakhInput(form.salary_currency) ? ' — enter in ₹ Lakhs' : ''}</label>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 100px', gap: '0.75rem' }}>
                  <select data-testid="job-salary-currency" value={form.salary_currency} onChange={(e) => handleSalaryCurrencyChange(e.target.value)} style={inputStyle}>
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                  </select>
                  <input data-testid="job-salary-min" type="number" min="0" step={usesLakhInput(form.salary_currency) ? '0.01' : '1'} value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} placeholder={usesLakhInput(form.salary_currency) ? 'Min (L)' : 'Min'} style={inputStyle} />
                  <input data-testid="job-salary-max" type="number" min="0" step={usesLakhInput(form.salary_currency) ? '0.01' : '1'} value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} placeholder={usesLakhInput(form.salary_currency) ? 'Max (L)' : 'Max'} style={inputStyle} />
                  <select data-testid="job-salary-period" value={form.salary_period} onChange={(e) => setForm({ ...form, salary_period: e.target.value })} style={inputStyle}>
                    {SALARY_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.short}</option>)}
                  </select>
                </div>
                {usesLakhInput(form.salary_currency) && (form.salary_min || form.salary_max) && (
                  <div data-testid="salary-lakh-hint" style={{ fontSize: '0.6875rem', color: '#0F766E', marginTop: '0.35rem', fontWeight: 600 }}>
                    ≈ {inputHint(form.salary_min, form.salary_currency) || '—'} – {inputHint(form.salary_max, form.salary_currency) || '—'}
                  </div>
                )}
              </div>
              <div style={{ borderTop: '1px solid #ECE7DE', paddingTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }}>Stage Benchmark Scores</label>
                <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.625rem' }}>
                  Minimum score (0&ndash;100) a candidate must meet to be <strong>eligible to advance</strong> into each stage. Candidates are never auto-advanced &mdash; recruiters decide. Leave defaults or adjust per role.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Screening</label>
                    <input data-testid="benchmark-screening-input" type="number" min="0" max="100" value={form.benchmark_screening} onChange={(e) => setForm({ ...form, benchmark_screening: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Assessment</label>
                    <input data-testid="benchmark-assessment-input" type="number" min="0" max="100" value={form.benchmark_assessment} onChange={(e) => setForm({ ...form, benchmark_assessment: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Interview</label>
                    <input data-testid="benchmark-interview-input" type="number" min="0" max="100" value={form.benchmark_interview} onChange={(e) => setForm({ ...form, benchmark_interview: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>BGV / Offer</label>
                    <input data-testid="benchmark-bgv-input" type="number" min="0" max="100" value={form.benchmark_bgv} onChange={(e) => setForm({ ...form, benchmark_bgv: e.target.value })} style={inputStyle} />
                  </div>
                </div>
              </div>
              <ScoringSettings form={form} setForm={setForm} />
              <DialogFooter style={{ display: 'flex', gap: '0.5rem' }}>
                {parsedFile && (
                  <Button type="button" variant="outline" onClick={() => { resetDialog(); }} style={{ borderRadius: 10 }}>
                    Upload Different File
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => { setShowCreate(false); resetDialog(); }} style={{ borderRadius: 10 }}>Cancel</Button>
                <Button data-testid="submit-job-button" type="submit" disabled={creating} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                  {creating ? 'Creating...' : 'Create Job'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
